import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ApiService, ELO_VISIBLE } from '../services/apiService';
import { eloNumberTitle, formatEloNumber, formatRatedClockLabel } from '../utils/eloUtils';
import './PlayersPage.css';

interface Player {
  id: number;
  username: string;
  wins: number;
  losses: number;
  draws: number;
  current_streak: number;
  streak_type: string | null;
  is_online: boolean;
  in_game: boolean;
  created_at: string;
  email_verified: boolean;
  country_code?: string | null;
  country_name?: string | null;
  dm_privacy?: string;
  elo_rating?: number;
  rated_games_count?: number;
  elo_fast?: number;
  elo_standard?: number;
  rated_games_fast?: number;
  rated_games_standard?: number;
}

function PlayerEloDisplay({
  player,
  size = 'default',
}: {
  player: Pick<Player, 'elo_rating' | 'rated_games_count' | 'elo_fast' | 'elo_standard' | 'rated_games_fast' | 'rated_games_standard'>;
  size?: 'compact' | 'default' | 'header';
}) {
  const standard = player.elo_standard ?? player.elo_rating;
  const fast = player.elo_fast;
  if (standard == null && fast == null) return null;
  const fontSize =
    size === 'compact' ? '0.8rem' : size === 'header' ? '1.05rem' : '0.95rem';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'baseline',
        gap: '10px',
        fontSize,
        flexWrap: 'wrap',
      }}
    >
      <span title={eloNumberTitle('standard', player.rated_games_standard ?? player.rated_games_count) || 'Standard rating (over 3 minutes)'}>
        <span style={{ color: '#6c757d', fontWeight: 500, fontSize: '0.8em' }}>Std </span>
        <span style={{ color: '#667eea', fontWeight: 600 }}>
          {formatEloNumber(standard, player.rated_games_standard ?? player.rated_games_count)}
        </span>
      </span>
      <span title={eloNumberTitle('fast', player.rated_games_fast) || 'Fast rating (3 minutes and below)'}>
        <span style={{ color: '#6c757d', fontWeight: 500, fontSize: '0.8em' }}>Fast </span>
        <span style={{ color: '#667eea', fontWeight: 600 }}>
          {formatEloNumber(fast ?? 1200, player.rated_games_fast)}
        </span>
      </span>
    </span>
  );
}

interface Challenge {
  id: number;
  challenger_id: number;
  challenger_username: string;
  challenged_id: number;
  challenged_username: string;
  status: string;
  created_at: string;
  expires_at: string | null;
  game_id: string | null;
}

interface PlayersPageProps {
  onBack: () => void;
  authState?: {
    isAuthenticated: boolean;
    user: {
      id: string;
      username: string;
      email: string;
    } | null;
  };
  onGameStart?: () => void; // Callback to close modal when game starts
  socket?: any; // Socket.IO connection for listening to challenge events
  onViewPlayerStats?: (userId: number, username: string) => void; // Callback to view another player's stats
  onWatchGame?: (gameId: string) => void; // Callback to watch a game in YugoVision
  onMessagePlayer?: (recipientId: number, recipientUsername: string) => void;
}

const PlayersPage: React.FC<PlayersPageProps> = ({ onBack, authState, onGameStart, socket, onViewPlayerStats, onWatchGame, onMessagePlayer }) => {
  const [players, setPlayers] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [challengeTimerPreference, setChallengeTimerPreference] = useState<1 | 2 | 3 | 5 | 10 | 15 | 30 | 60>(10); // Timer preference for challenges
  const [challengeIncrementPreference, setChallengeIncrementPreference] = useState<0 | 1 | 2 | 3 | 5 | 10 | 15>(0);
  const [challengeIsRated, setChallengeIsRated] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [challenging, setChallenging] = useState(false);
  const [challengeMessage, setChallengeMessage] = useState<{ type: 'success' | 'error'; text: string; gameId?: string | null } | null>(null);
  const [challengeSentTo, setChallengeSentTo] = useState<string | null>(null); // Track who challenge was sent to
  const [challengeSentId, setChallengeSentId] = useState<number | null>(null); // Track challenge ID when sent
  const [challengeSentTime, setChallengeSentTime] = useState<number | null>(null); // Track when challenge was sent (for grace period)
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loadingChallenges, setLoadingChallenges] = useState(false);
  const [showChallenges, setShowChallenges] = useState(false);
  const [blockedUsers, setBlockedUsers] = useState<number[]>([]); // Array of blocked user IDs
  const [blockedUsersList, setBlockedUsersList] = useState<Array<{id: number; username: string; blocked_at: string}>>([]); // Full blocked users list
  const [loadingBlocks, setLoadingBlocks] = useState(false);
  const [blocking, setBlocking] = useState(false);
  const [showBlockedUsers, setShowBlockedUsers] = useState(false);
  const [challengeDeclinedInfo, setChallengeDeclinedInfo] = useState<{ username: string; reason: 'declined' | 'timeout' } | null>(null);
  
  // Ref to track last challenge API call time to prevent rapid-fire requests
  const lastChallengesCallRef = useRef<number>(0);
  const CHALLENGES_CALL_DEBOUNCE_MS = 2000; // Minimum 2 seconds between calls
  const CHALLENGE_TIMEOUT_MS = 32000; // Server expires at 30s; small buffer if the event is missed
  const challengeSentToRef = useRef<string | null>(null);
  const challengeSentIdRef = useRef<number | null>(null);
  const declineHandledRef = useRef(false);
  const declineClearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    challengeSentToRef.current = challengeSentTo;
  }, [challengeSentTo]);
  useEffect(() => {
    challengeSentIdRef.current = challengeSentId;
  }, [challengeSentId]);

  useEffect(() => {
    return () => {
      if (declineClearTimerRef.current) {
        clearTimeout(declineClearTimerRef.current);
      }
    };
  }, []);

  const loadPlayers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await ApiService.getPlayers(searchTerm || undefined, onlineOnly);
      if (result.error) {
        setError(result.error);
      } else {
        setPlayers(result.players);
      }
    } catch (err) {
      setError('Failed to load players');
    } finally {
      setLoading(false);
    }
  }, [searchTerm, onlineOnly]);

  useEffect(() => {
    loadPlayers();
  }, [loadPlayers]);

  const loadChallenges = useCallback(async () => {
    // Prevent rapid-fire calls - debounce if called too soon after last call
    const now = Date.now();
    const timeSinceLastCall = now - lastChallengesCallRef.current;
    if (timeSinceLastCall < CHALLENGES_CALL_DEBOUNCE_MS && lastChallengesCallRef.current > 0) {
      console.log(`Skipping challenge load - too soon (${timeSinceLastCall}ms ago)`);
      return;
    }
    
    lastChallengesCallRef.current = now;
    setLoadingChallenges(true);
    try {
      const result = await ApiService.getChallenges();
      if (result.error) {
        console.error('Error loading challenges:', result.error);
      } else {
        setChallenges(result.challenges);
      }
    } catch (err) {
      console.error('Failed to load challenges:', err);
    } finally {
      setLoadingChallenges(false);
    }
  }, []);

  const applyChallengeGone = useCallback((username: string, reason: 'declined' | 'timeout') => {
    if (declineHandledRef.current) return;
    declineHandledRef.current = true;
    setChallengeDeclinedInfo({ username, reason });
    if (!challengeSentToRef.current) {
      setChallengeSentTo(username);
    }
    loadChallenges();
    if (declineClearTimerRef.current) {
      clearTimeout(declineClearTimerRef.current);
    }
    declineClearTimerRef.current = setTimeout(() => {
      setChallengeDeclinedInfo(null);
      setChallengeSentTo(null);
      setChallengeSentId(null);
      setChallengeSentTime(null);
      declineClearTimerRef.current = null;
    }, 5000);
  }, [loadChallenges]);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      loadChallenges();
      // Refresh challenges every 10 seconds
      const interval = setInterval(loadChallenges, 10000);
      return () => clearInterval(interval);
    }
  }, [loadChallenges, authState?.isAuthenticated]);

  // Listen for challenge declined events
  useEffect(() => {
    if (!socket) return;

    const handleChallengeDeclined = (data: { challengeId: number; challengedUsername: string; reason: string }) => {
      console.log('📨 Challenge declined notification in PlayersPage:', data);
      const isOurChallenge =
        (challengeSentIdRef.current != null && challengeSentIdRef.current === data.challengeId) ||
        (challengeSentToRef.current != null && challengeSentToRef.current === data.challengedUsername);
      if (isOurChallenge) {
        applyChallengeGone(data.challengedUsername, data.reason === 'timeout' ? 'timeout' : 'declined');
      }
    };

    socket.on('challengeDeclined', handleChallengeDeclined);

    return () => {
      socket.off('challengeDeclined', handleChallengeDeclined);
    };
  }, [socket, applyChallengeGone]);

  // If the server event is missed, unstick Waiting shortly after the 30s expiry.
  useEffect(() => {
    if (!challengeSentTo || !challengeSentTime) return;
    const remaining = CHALLENGE_TIMEOUT_MS - (Date.now() - challengeSentTime);
    const timer = setTimeout(() => {
      const username = challengeSentToRef.current;
      if (!username) return;
      applyChallengeGone(username, 'timeout');
    }, Math.max(0, remaining));
    return () => clearTimeout(timer);
  }, [challengeSentTo, challengeSentTime, applyChallengeGone]);

  // Compute challenge arrays (needed for useEffect below)
  const currentUserId = useMemo(() => 
    authState?.user?.id ? parseInt(authState.user.id) : null,
    [authState?.user?.id]
  );
  const receivedChallenges = useMemo(() => 
    currentUserId 
      ? challenges.filter(c => c.challenged_id === currentUserId)
      : [],
    [challenges, currentUserId]
  );
  const sentChallenges = useMemo(() =>
    currentUserId
      ? challenges.filter(c => c.challenger_id === currentUserId)
      : [],
    [challenges, currentUserId]
  );

  // Reset challenge sent status if the challenge was declined or expired (but NOT if just sent)
  // CRITICAL: Add grace period and check by challenge ID to prevent race conditions
  useEffect(() => {
    if (selectedPlayer && challengeSentTo === selectedPlayer.username) {
      // Grace period: Don't check for 3 seconds after sending to allow challenge to appear in backend
      if (challengeSentTime && Date.now() - challengeSentTime < 3000) {
        return; // Too soon, wait for grace period
      }
      
      // Compute sent challenges for current user
      const userSentChallenges = currentUserId
        ? challenges.filter(c => c.challenger_id === currentUserId)
        : [];
      
      // If we have a challenge ID, check for that specific challenge first
      if (challengeSentId) {
        const specificChallenge = userSentChallenges.find(c => c.id === challengeSentId);
        if (specificChallenge) {
          // Challenge found - check its status
          if (specificChallenge.status === 'pending') {
            // Still pending, keep challengeSentTo set
            return;
          }
          if (specificChallenge.game_id !== null) {
            // Challenge accepted, reset
            setChallengeSentTo(null);
            setChallengeSentId(null);
            setChallengeSentTime(null);
            return;
          }
          if (specificChallenge.status === 'declined' || specificChallenge.status === 'expired') {
            applyChallengeGone(selectedPlayer.username, specificChallenge.status === 'expired' ? 'timeout' : 'declined');
            return;
          }
        } else if (challengeSentTime && Date.now() - challengeSentTime >= CHALLENGE_TIMEOUT_MS) {
          // Pending list no longer includes this challenge (expired/declined). Unstick Waiting.
          applyChallengeGone(selectedPlayer.username, 'timeout');
          return;
        } else {
          return;
        }
      }
      
      // Fallback: Check if there's any challenge to this player that is still pending
      const hasPendingChallenge = userSentChallenges.some(
        challenge => challenge.challenged_username === selectedPlayer.username && 
                     challenge.status === 'pending'
      );
      
      // Check if challenge was accepted and game started (has game_id)
      const challengeAccepted = userSentChallenges.some(
        challenge => challenge.challenged_username === selectedPlayer.username && 
                     challenge.game_id !== null
      );
      
      // Only reset if we have challenges loaded AND no pending challenge AND not accepted
      // AND grace period has passed AND we don't have a challengeSentId (if we do, wait for it)
      if (challenges.length > 0 && !hasPendingChallenge && !challengeAccepted && challengeSentTime && Date.now() - challengeSentTime >= 3000 && !challengeSentId) {
        setChallengeSentTo(null);
        setChallengeSentId(null);
        setChallengeSentTime(null);
      }
    }
  }, [challenges, currentUserId, selectedPlayer, challengeSentTo, challengeSentId, challengeSentTime, applyChallengeGone]);

  // Close profile modal when a challenge is accepted and game starts
  useEffect(() => {
    if (!selectedPlayer || !currentUserId) return;
    
    // Check if any challenge sent to the selected player has a game_id (game started)
    // This works regardless of challenge status, as game_id is set when game starts
    const challengeWithGame = challenges.find(
      challenge => 
        challenge.challenger_id === currentUserId &&
        challenge.challenged_username === selectedPlayer.username &&
        challenge.game_id !== null
    );
    
    // If challenge has a game_id, the game has started - close the profile modal immediately
    if (challengeWithGame) {
      console.log('✅ Challenge accepted and game started - closing profile modal');
      setSelectedPlayer(null);
      setChallengeSentTo(null);
      setChallengeSentId(null);
      setChallengeSentTime(null);
      setChallengeMessage(null);
      // Also call the callback if provided (for immediate closure from App.tsx)
      if (onGameStart) {
        onGameStart();
      }
    }
  }, [challenges, currentUserId, selectedPlayer, onGameStart]);
  
  // Expose function to close modal externally (for App.tsx to call when gameStart event fires)
  useEffect(() => {
    if (selectedPlayer) {
      // Store the close function reference so App.tsx can call it when game starts
      (window as any).__closePlayersPageModal = () => {
        setSelectedPlayer(null);
        setChallengeSentTo(null);
        setChallengeSentId(null);
        setChallengeSentTime(null);
        setChallengeMessage(null);
      };
      return () => {
        delete (window as any).__closePlayersPageModal;
      };
    }
  }, [selectedPlayer]);

  const loadBlockedUsers = useCallback(async () => {
    setLoadingBlocks(true);
    try {
      const result = await ApiService.getBlockedUsers();
      if (result.success) {
        setBlockedUsers(result.blocks.map((block: any) => block.id));
        setBlockedUsersList(result.blocks);
      }
    } catch (err) {
      console.error('Failed to load blocked users:', err);
    } finally {
      setLoadingBlocks(false);
    }
  }, []);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      loadBlockedUsers();
    }
  }, [loadBlockedUsers, authState?.isAuthenticated]);

  const handlePlayerClick = (player: Player) => {
    setSelectedPlayer(player);
  };

  const handleCloseProfile = () => {
    setSelectedPlayer(null);
    setChallengeMessage(null);
    setChallengeSentTo(null); // Reset challenge sent status when closing profile
    setChallengeSentId(null);
    setChallengeSentTime(null);
  };

  const handleBlock = async () => {
    if (!selectedPlayer) return;
    
    setBlocking(true);
    setChallengeMessage(null);
    
    try {
      const result = await ApiService.blockUser(selectedPlayer.id);
      
      if (result.success) {
        setChallengeMessage({ type: 'success', text: `${selectedPlayer.username} has been blocked` });
        await loadBlockedUsers(); // Refresh blocked users list
        handleCloseProfile(); // Close modal after blocking
      } else {
        setChallengeMessage({ type: 'error', text: result.error || 'Failed to block user' });
      }
    } catch (err) {
      setChallengeMessage({ type: 'error', text: 'Failed to block user' });
    } finally {
      setBlocking(false);
    }
  };

  const handleUnblock = async (userId?: number) => {
    const userIdToUnblock = userId || selectedPlayer?.id;
    if (!userIdToUnblock) return;
    
    setBlocking(true);
    setChallengeMessage(null);
    
    try {
      const result = await ApiService.unblockUser(userIdToUnblock);
      
      if (result.success) {
        const unblockedUser = blockedUsersList.find(b => b.id === userIdToUnblock);
        const username = unblockedUser?.username || selectedPlayer?.username || 'User';
        setChallengeMessage({ type: 'success', text: `${username} has been unblocked` });
        await loadBlockedUsers(); // Refresh blocked users list
        if (!userId) {
          handleCloseProfile(); // Close modal after unblocking (only if from modal)
        }
      } else {
        setChallengeMessage({ type: 'error', text: result.error || 'Failed to unblock user' });
      }
    } catch (err) {
      setChallengeMessage({ type: 'error', text: 'Failed to unblock user' });
    } finally {
      setBlocking(false);
    }
  };

  const handleChallenge = async () => {
    if (!selectedPlayer) return;
    
    setChallenging(true);
    setChallengeMessage(null);
    
    // CRITICAL: Set challenge sent status IMMEDIATELY so button text changes right away
    setChallengeSentTo(selectedPlayer.username);
    setChallengeSentTime(Date.now()); // Track when challenge was sent for grace period
    declineHandledRef.current = false;
    if (declineClearTimerRef.current) {
      clearTimeout(declineClearTimerRef.current);
      declineClearTimerRef.current = null;
    }
    
    try {
      const timerSettings = {
        minutesPerPlayer: challengeTimerPreference,
        incrementSeconds: challengeIncrementPreference
      };
      const result = await ApiService.sendChallenge(selectedPlayer.id, timerSettings, challengeIsRated);
      
      if (result.success) {
        // Store challenge ID if available for more reliable tracking
        if (result.challenge && result.challenge.id) {
          setChallengeSentId(result.challenge.id);
        }
        loadChallenges(); // Refresh challenges list
      } else {
        // If API call failed, reset the button text and show error
        setChallengeSentTo(null);
        setChallengeSentId(null);
        setChallengeSentTime(null);
        // Show user-friendly error messages
        let errorMessage = result.error || 'Failed to send challenge';
        let activeGameId: string | null = null;
        if (result.error?.includes('already considering a challenge')) {
          errorMessage = `${selectedPlayer.username} is already considering another challenge. Please wait for them to respond.`;
        } else if (result.error?.includes('already have a pending challenge')) {
          errorMessage = 'You already have a pending challenge with this player. Please wait for them to respond.';
        } else if (result.error?.includes('not online')) {
          errorMessage = `${selectedPlayer.username} is not online.`;
        } else if (result.error?.includes('already in a game')) {
          errorMessage = `${selectedPlayer.username} is currently in a game.`;
          activeGameId = result.gameId || null;
        }
        setChallengeMessage({ type: 'error', text: errorMessage, gameId: activeGameId });
      }
    } catch (err) {
      // If API call failed, reset the button text and show error
      setChallengeSentTo(null);
      setChallengeSentId(null);
      setChallengeSentTime(null);
      setChallengeMessage({ type: 'error', text: 'Failed to send challenge' });
    } finally {
      setChallenging(false);
    }
  };

  const handleAcceptChallenge = async (challengeId: number) => {
    try {
      // Note: Backend will also check if user is in game, but this provides better UX
      const result = await ApiService.acceptChallenge(challengeId);
      if (result.success) {
        loadChallenges();
        // Game will start automatically via Socket.IO gameStart event
        // The App.tsx will handle navigation to the game
      } else {
        alert(result.error || 'Failed to accept challenge');
      }
    } catch (err: any) {
      alert(err?.response?.data?.error || 'Failed to accept challenge');
    }
  };

  const handleDeclineChallenge = async (challengeId: number) => {
    try {
      const result = await ApiService.declineChallenge(challengeId);
      if (result.success) {
        loadChallenges();
      } else {
        alert(result.error || 'Failed to decline challenge');
      }
    } catch (err) {
      alert('Failed to decline challenge');
    }
  };

  const pendingChallengesCount = receivedChallenges.length;

  // Sort players: divide by online/offline, each sorted alphabetically (case-insensitive)
  const sortedPlayers = useMemo(() => {
    const online = players.filter((p) => p.is_online);
    const offline = players.filter((p) => !p.is_online);
    const sortByName = (a: Player, b: Player) =>
      a.username.localeCompare(b.username, undefined, { sensitivity: 'base' });
    return {
      online: [...online].sort(sortByName),
      offline: [...offline].sort(sortByName),
    };
  }, [players]);

  return (
    <div className="players-page">
      {/* Fixed Header Section */}
      <div className="players-page-header-fixed">
        <h1>Players</h1>
        <div className="players-search-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by username..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-controls">
            <label className="online-filter">
              <input
                type="checkbox"
                checked={onlineOnly}
                onChange={(e) => setOnlineOnly(e.target.checked)}
              />
              <span>Online Only</span>
            </label>
            {authState?.isAuthenticated && (
              <button 
                className="blocked-users-btn-small"
                onClick={() => setShowBlockedUsers(!showBlockedUsers)}
              >
                🚫 Blocked {blockedUsersList.length > 0 && `(${blockedUsersList.length})`}
              </button>
            )}
          </div>
        </div>
        {pendingChallengesCount > 0 && (
          <button 
            className="challenges-notification-btn"
            onClick={() => setShowChallenges(!showChallenges)}
          >
            🔔 Challenges ({pendingChallengesCount})
          </button>
        )}
      </div>

      {/* Scrollable Content Section */}
      <div className="players-page-content-scrollable">

        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
            <button onClick={loadPlayers} className="retry-btn">
              🔄 Retry
            </button>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="loading-message">Loading players...</div>
        )}

        {/* Challenges Section */}
        {showChallenges && authState?.isAuthenticated && (
          <div className="challenges-section">
            <h3>Challenges</h3>
            
            {/* Received Challenges */}
            {receivedChallenges.length > 0 && (
              <div className="challenges-group">
                <h4>Received ({receivedChallenges.length})</h4>
                {receivedChallenges.map((challenge) => (
                  <div key={challenge.id} className="challenge-item">
                    <div className="challenge-info">
                      <span className="challenge-from">{challenge.challenger_username}</span>
                      <span className="challenge-time">challenged you</span>
                    </div>
                    <div className="challenge-actions">
                      <button 
                        className="accept-btn"
                        onClick={() => handleAcceptChallenge(challenge.id)}
                      >
                        ✓ Accept
                      </button>
                      <button 
                        className="decline-btn"
                        onClick={() => handleDeclineChallenge(challenge.id)}
                      >
                        ✗ Decline
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Sent Challenges */}
            {sentChallenges.length > 0 && (
              <div className="challenges-group">
                <h4>Sent ({sentChallenges.length})</h4>
                {sentChallenges.map((challenge) => (
                  <div key={challenge.id} className="challenge-item">
                    <div className="challenge-info">
                      <span className="challenge-to">Waiting for {challenge.challenged_username}</span>
                      <span className="challenge-status">Pending...</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {receivedChallenges.length === 0 && sentChallenges.length === 0 && (
              <div className="no-challenges">No pending challenges</div>
            )}
          </div>
        )}

        {/* Blocked Users Section */}
        {showBlockedUsers && authState?.isAuthenticated && (
          <div className="blocked-users-section">
            <h3>Blocked Users</h3>
            
            {loadingBlocks ? (
              <div className="loading-message">Loading blocked users...</div>
            ) : blockedUsersList.length > 0 ? (
              <div className="blocked-users-list">
                {blockedUsersList.map((blockedUser) => (
                  <div key={blockedUser.id} className="blocked-user-item">
                    <div className="blocked-user-info">
                      <span className="blocked-username">{blockedUser.username}</span>
                      <span className="blocked-time">
                        Blocked {new Date(blockedUser.blocked_at).toLocaleDateString()}
                      </span>
                    </div>
                    <button 
                      className="unblock-btn-small"
                      onClick={() => handleUnblock(blockedUser.id)}
                      disabled={blocking}
                    >
                      {blocking ? 'Unblocking...' : '🔓 Unblock'}
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-blocked-users">No blocked users</div>
            )}
          </div>
        )}

        {/* Players List */}
        {!loading && !error && (
          <div className="players-list">
            {players.length === 0 ? (
              <div className="no-players">
                {searchTerm || onlineOnly 
                  ? 'No players found matching your criteria'
                  : 'No players available'}
              </div>
            ) : (
              <>
                {sortedPlayers.online.length > 0 && (
                  <div className="players-section">
                    <h4 className="players-section-title">Online</h4>
                    {sortedPlayers.online.map((player) => (
                      <div
                        key={player.id}
                        className="player-item"
                        onClick={() => handlePlayerClick(player)}
                      >
                        <div className="player-info">
                          <span className={`status-dot ${player.is_online ? 'online' : 'offline'}`}></span>
                          <span className="player-username">{player.username}</span>
                          {ELO_VISIBLE && <PlayerEloDisplay player={player} size="compact" />}
                          {player.in_game && <span className="in-game-badge">🎮 In Game</span>}
                          {authState?.isAuthenticated && (
                            <button
                              className="view-stats-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onViewPlayerStats) {
                                  onViewPlayerStats(player.id, player.username);
                                }
                              }}
                              title="View Full Statistics"
                            >
                              📊 Stats
                            </button>
                          )}
                        </div>
                        <div className="player-stats-mini">
                          {player.wins}W / {player.losses}L / {player.draws}D
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {sortedPlayers.offline.length > 0 && (
                  <div className="players-section">
                    <h4 className="players-section-title">Offline</h4>
                    {sortedPlayers.offline.map((player) => (
                      <div
                        key={player.id}
                        className="player-item"
                        onClick={() => handlePlayerClick(player)}
                      >
                        <div className="player-info">
                          <span className={`status-dot ${player.is_online ? 'online' : 'offline'}`}></span>
                          <span className="player-username">{player.username}</span>
                          {ELO_VISIBLE && <PlayerEloDisplay player={player} size="compact" />}
                          {player.in_game && <span className="in-game-badge">🎮 In Game</span>}
                          {authState?.isAuthenticated && (
                            <button
                              className="view-stats-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onViewPlayerStats) {
                                  onViewPlayerStats(player.id, player.username);
                                }
                              }}
                              title="View Full Statistics"
                            >
                              📊 Stats
                            </button>
                          )}
                        </div>
                        <div className="player-stats-mini">
                          {player.wins}W / {player.losses}L / {player.draws}D
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Player Profile Modal */}
      {selectedPlayer && (
        <div className="player-profile-modal-overlay" onClick={handleCloseProfile}>
          <div className="player-profile-modal" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={handleCloseProfile}>×</button>
            <div className="player-profile-header">
              <span className={`status-dot-large ${selectedPlayer.is_online ? 'online' : 'offline'}`}></span>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', flex: 1, minWidth: 0 }}>
                <h2 style={{ margin: 0 }}>{selectedPlayer.username}</h2>
                {ELO_VISIBLE && <PlayerEloDisplay player={selectedPlayer} size="header" />}
              </div>
              {selectedPlayer.in_game && <span className="in-game-badge-large">🎮 In Game</span>}
            </div>
            <div className="player-profile-stats">
              {ELO_VISIBLE && (
                <div className="stat-item">
                  <span className="stat-label">Rating:</span>
                  <span className="stat-value">
                    {selectedPlayer.elo_rating != null ? (
                      <PlayerEloDisplay player={selectedPlayer} size="default" />
                    ) : (
                      '—'
                    )}
                  </span>
                </div>
              )}
              <div className="stat-item">
                <span className="stat-label">Wins:</span>
                <span className="stat-value">{selectedPlayer.wins}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Losses:</span>
                <span className="stat-value">{selectedPlayer.losses}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Draws:</span>
                <span className="stat-value">{selectedPlayer.draws}</span>
              </div>
              {selectedPlayer.current_streak > 0 && (
                <div className="stat-item">
                  <span className="stat-label">Current Streak:</span>
                  <span className="stat-value">{selectedPlayer.current_streak} {selectedPlayer.streak_type === 'win' ? 'Wins' : 'Losses'}</span>
                </div>
              )}
              <div className="stat-item">
                <span className="stat-label">Member Since:</span>
                <span className="stat-value">{new Date(selectedPlayer.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
              </div>
            </div>
            
            {/* Timer Selection for Challenge */}
            {!blockedUsers.includes(selectedPlayer.id) && selectedPlayer.is_online && (
              <div style={{ margin: '15px 0', padding: '12px', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '6px' }}>
                <div style={{ fontSize: '0.9rem', color: '#495057', marginBottom: '12px', textAlign: 'center' }}>
                  <strong>⏱️ Time Control</strong>
                </div>
                <div style={{ marginBottom: '12px' }}>
                  <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '6px', textAlign: 'center' }}>Minutes:</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      {([1, 2, 3, 5] as const).map((minutes) => (
                        <button
                          key={minutes}
                          type="button"
                          onClick={() => setChallengeTimerPreference(minutes)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: `2px solid ${challengeTimerPreference === minutes ? '#007bff' : '#ccc'}`,
                            background: challengeTimerPreference === minutes ? '#007bff' : 'white',
                            color: challengeTimerPreference === minutes ? 'white' : '#333',
                            fontWeight: challengeTimerPreference === minutes ? 'bold' : 'normal',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            minWidth: '70px'
                          }}
                        >
                          {minutes} min
                        </button>
                      ))}
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                      {([10, 15, 30, 60] as const).map((minutes) => (
                        <button
                          key={minutes}
                          type="button"
                          onClick={() => setChallengeTimerPreference(minutes)}
                          style={{
                            padding: '8px 16px',
                            borderRadius: '6px',
                            border: `2px solid ${challengeTimerPreference === minutes ? '#007bff' : '#ccc'}`,
                            background: challengeTimerPreference === minutes ? '#007bff' : 'white',
                            color: challengeTimerPreference === minutes ? 'white' : '#333',
                            fontWeight: challengeTimerPreference === minutes ? 'bold' : 'normal',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            minWidth: '70px'
                          }}
                        >
                          {minutes} min
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', color: '#6c757d', marginBottom: '6px', textAlign: 'center' }}>Increment:</div>
                  <div style={{ display: 'flex', gap: '8px', justifyContent: 'center', flexWrap: 'wrap' }}>
                    {([0, 1, 2, 3, 5, 10, 15] as const).map((increment) => (
                      <button
                        key={increment}
                        type="button"
                        onClick={() => setChallengeIncrementPreference(increment)}
                        style={{
                          padding: '8px 16px',
                          borderRadius: '6px',
                          border: `2px solid ${challengeIncrementPreference === increment ? '#007bff' : '#ccc'}`,
                          background: challengeIncrementPreference === increment ? '#007bff' : 'white',
                          color: challengeIncrementPreference === increment ? 'white' : '#333',
                          fontWeight: challengeIncrementPreference === increment ? 'bold' : 'normal',
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          minWidth: '50px'
                        }}
                      >
                        {increment}s
                      </button>
                    ))}
                  </div>
                </div>
                {ELO_VISIBLE && (
                  <div style={{ marginTop: '12px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '8px', marginBottom: '8px' }}>
                      <button
                        type="button"
                        onClick={() => setChallengeIsRated(true)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '6px',
                          border: 'none',
                          background: challengeIsRated ? '#667eea' : '#e9ecef',
                          color: challengeIsRated ? 'white' : '#495057',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Rated
                      </button>
                      <button
                        type="button"
                        onClick={() => setChallengeIsRated(false)}
                        style={{
                          padding: '6px 14px',
                          borderRadius: '6px',
                          border: 'none',
                          background: !challengeIsRated ? '#667eea' : '#e9ecef',
                          color: !challengeIsRated ? 'white' : '#495057',
                          cursor: 'pointer',
                          fontSize: '0.85rem'
                        }}
                      >
                        Casual
                      </button>
                    </div>
                    <div style={{ fontSize: '0.8rem', color: '#495057' }}>
                      {formatRatedClockLabel(challengeTimerPreference, challengeIncrementPreference, challengeIsRated)}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Challenge Button */}
            <div className="player-profile-actions">
              {blockedUsers.includes(selectedPlayer.id) ? (
                <button 
                  className="unblock-button" 
                  onClick={() => handleUnblock()}
                  disabled={blocking}
                >
                  {blocking ? 'Unblocking...' : '🔓 Unblock User'}
                </button>
              ) : !selectedPlayer.is_online ? (
                <>
                  <div className="offline-message">
                    Player is not online
                  </div>
                  {onMessagePlayer && (() => {
                    const dmsDisabled = selectedPlayer.dm_privacy === 'nobody';
                    return dmsDisabled ? (
                      <button className="message-button" disabled title="This player is not accepting messages">
                        💬 Messaging disabled
                      </button>
                    ) : (
                      <button
                        className="message-button"
                        onClick={() => onMessagePlayer(selectedPlayer.id, selectedPlayer.username)}
                      >
                        💬 Message {selectedPlayer.username}
                      </button>
                    );
                  })()}
                </>
              ) : (
                <>
                  <button 
                    className={`challenge-button ${challengeSentTo ? 'challenge-sent' : ''} ${challengeDeclinedInfo && challengeDeclinedInfo.username === selectedPlayer.username ? 'challenge-declined' : ''}`}
                    onClick={handleChallenge}
                    disabled={challenging || selectedPlayer.in_game || !!challengeSentTo}
                  >
                    {challenging 
                      ? 'Sending...' 
                      : challengeDeclinedInfo && challengeDeclinedInfo.username === selectedPlayer.username
                        ? `${selectedPlayer.username} ${challengeDeclinedInfo.reason === 'timeout' ? 'declined (timeout)' : 'declined your challenge'}`
                        : challengeSentTo 
                          ? `Waiting for ${challengeSentTo} to Accept Challenge` 
                          : selectedPlayer.in_game 
                            ? 'Player In Game' 
                            : `Challenge ${selectedPlayer.username} to a Game`}
                  </button>
                  {onMessagePlayer && (() => {
                    const dmsDisabled = selectedPlayer.dm_privacy === 'nobody';
                    return dmsDisabled ? (
                      <button className="message-button" disabled title="This player is not accepting messages">
                        💬 Messaging disabled
                      </button>
                    ) : (
                      <button
                        className="message-button"
                        onClick={() => onMessagePlayer(selectedPlayer.id, selectedPlayer.username)}
                      >
                        💬 Message {selectedPlayer.username}
                      </button>
                    );
                  })()}
                  <button 
                    className="block-button" 
                    onClick={handleBlock}
                    disabled={blocking}
                  >
                    {blocking ? 'Blocking...' : '🚫 Block User'}
                  </button>
                </>
              )}
              
              {/* Only show error messages in separate container, success messages are in button */}
              {challengeMessage && challengeMessage.type === 'error' && (
                <div className={`challenge-message ${challengeMessage.type}`}>
                  {challengeMessage.text}
                  {challengeMessage.gameId && onWatchGame && (
                    <button
                      className="watch-game-button"
                      onClick={() => onWatchGame(challengeMessage.gameId!)}
                    >
                      Watch in YugoVision
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default PlayersPage;

